import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { Trophy, Timer, CreditCard, Phone, Plus, ArrowRight } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../components/auth/AuthProvider';
import type { Campaign } from '../lib/supabase';

const LandingPage = () => {
  const [activeCampaign, setActiveCampaign] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    fetchActiveCampaign();
  }, []);

  const fetchActiveCampaign = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('campaigns')
        .select('*')
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) throw error;
      
      setActiveCampaign(data && data.length > 0 ? data[0] : null);
    } catch (error) {
      console.error('Error fetching active campaign:', error);
    } finally {
      setLoading(false);
    }
  };

  const entryUrl = activeCampaign
    ? `${window.location.origin}/entry/${activeCampaign.id}`
    : '';

  return (
    <div className="flex flex-col items-center justify-center space-y-8">
      <div className="text-center space-y-4">
        <h1 className="text-4xl font-bold text-white">Welcome to QRaffle</h1>
        <p className="text-xl text-white/80">Create and manage raffles or enter to win amazing prizes</p>
      </div>

      {user ? (
        <div className="flex gap-4">
          <Link
            to="/campaigns"
            className="flex items-center px-6 py-3 bg-white text-purple-600 rounded-md hover:bg-white/90 transition-colors"
          >
            <Plus className="h-5 w-5 mr-2" />
            Create New Raffle
          </Link>
          <Link
            to="/admin"
            className="flex items-center px-6 py-3 bg-purple-700 text-white rounded-md hover:bg-purple-800 transition-colors"
          >
            <ArrowRight className="h-5 w-5 mr-2" />
            View Dashboard
          </Link>
        </div>
      ) : (
        <div className="flex gap-4">
          <Link
            to="/register"
            className="flex items-center px-6 py-3 bg-white text-purple-600 rounded-md hover:bg-white/90 transition-colors"
          >
            <Plus className="h-5 w-5 mr-2" />
            Start Creating Raffles
          </Link>
          <Link
            to="/login"
            className="flex items-center px-6 py-3 bg-purple-700 text-white rounded-md hover:bg-purple-800 transition-colors"
          >
            <ArrowRight className="h-5 w-5 mr-2" />
            Sign In
          </Link>
        </div>
      )}

      {loading ? (
        <div className="bg-white/10 backdrop-blur-md p-8 rounded-xl border border-white/20">
          <p className="text-white">Loading raffle details...</p>
        </div>
      ) : activeCampaign ? (
        <div className="bg-white p-8 rounded-xl shadow-lg">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Active Raffle</h2>
            <p className="text-gray-600">Scan the QR code to enter</p>
          </div>
          <QRCodeSVG value={entryUrl} size={200} className="mx-auto mb-6" />
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-800">{activeCampaign.name}</h3>
            <p className="text-sm text-gray-600">
              Ends {new Date(activeCampaign.end_date).toLocaleDateString()}
            </p>
            <p className="text-sm text-purple-600 font-semibold mt-2">
              {activeCampaign.ticket_price.toFixed(2)} per ticket
            </p>
          </div>
        </div>
      ) : (
        <div className="bg-white/10 backdrop-blur-md p-8 rounded-xl border border-white/20">
          <p className="text-white text-center">No active raffle at the moment</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 w-full max-w-4xl mt-12">
        <FeatureCard
          icon={<Phone className="h-8 w-8" />}
          title="Easy Entry"
          description="Simply scan and enter your phone number"
        />
        <FeatureCard
          icon={<CreditCard className="h-8 w-8" />}
          title="Flexible Payment"
          description="Pay online or in person"
        />
        <FeatureCard
          icon={<Timer className="h-8 w-8" />}
          title="Real-time Updates"
          description="Get Email notifications throughout"
        />
        <FeatureCard
          icon={<Trophy className="h-8 w-8" />}
          title="Fair Drawing"
          description="Secure random winner selection"
        />
      </div>
    </div>
  );
};

const FeatureCard = ({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) => (
  <div className="bg-white/10 backdrop-blur-md p-6 rounded-lg border border-white/20">
    <div className="text-white">{icon}</div>
    <h3 className="text-xl font-semibold text-white mt-4">{title}</h3>
    <p className="text-white/80 mt-2">{description}</p>
  </div>
);

export default LandingPage;